import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Alert,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  GroupAdd as GroupAddIcon,
  Visibility as VisibilityIcon,
  Schedule as ScheduleIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon
} from '@mui/icons-material';
import teamService from '../services/teamService';
import { useNavigate } from 'react-router-dom';

interface TeamAccessRequest {
  id: number;
  teamId: number;
  teamName: string;
  status: string;
  requestedAt: string;
  message?: string;
}

interface TeamAccessRequestsPanelProps {
  onRequestAccess?: (teamId?: number) => void;
}

const TeamAccessRequestsPanel: React.FC<TeamAccessRequestsPanelProps> = ({ onRequestAccess }) => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState<TeamAccessRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadAccessRequests();
  }, []);

  const loadAccessRequests = async () => {
    try {
      setLoading(true);
      // Since this is provider-specific functionality, we'll implement a basic version
      // In a real implementation, this would fetch actual access requests from the backend
      // For now, we'll show a simple interface
      setRequests([]);
    } catch (err: any) {
      setError('Failed to load team access requests');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'approved':
        return <ApprovedIcon color="success" />;
      case 'rejected':
        return <RejectedIcon color="error" />;
      case 'pending':
      default:
        return <ScheduleIcon color="warning" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'pending':
      default:
        return 'warning';
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <GroupAddIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Team Access Management</Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<GroupAddIcon />}
            onClick={() => navigate('/groups/join')}
            size="small"
          >
            Browse Teams
          </Button>
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : requests.length > 0 ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Your team access requests:
            </Typography>
            <List>
              {requests.map((request, index) => (
                <React.Fragment key={request.id}>
                  <ListItem>
                    <ListItemText
                      primary={request.teamName}
                      secondary={
                        <Box>
                          <Typography variant="caption" display="block">
                            Requested: {new Date(request.requestedAt).toLocaleDateString()}
                          </Typography>
                          {request.message && (
                            <Typography variant="caption" color="text.secondary">
                              {request.message}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          icon={getStatusIcon(request.status)}
                          label={request.status}
                          size="small"
                          color={getStatusColor(request.status) as any}
                          variant="outlined"
                        />
                        <IconButton
                          edge="end"
                          onClick={() => navigate(`/teams/${request.teamId}`)}
                          size="small"
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < requests.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              You haven't made any team access requests yet. Browse available teams to request access.
            </Typography>
          </Alert>
        )}

        <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            As a provider, you can request access to teams to collaborate with students and other providers. 
            Use the "Browse Teams" button above to find teams you'd like to join.
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default TeamAccessRequestsPanel;